import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { InfoIconControl } from "@/Common/Components/IconControls/InfoIconControl";
import { MyleTableColumnInit } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { Patient } from "@/Common/Models/Patient/Patient";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { DocumentViewSettingPopup } from "@/Documents/Components/DocumentViewSetting/DocumentViewSettingPopup";
import { MedicalDocumentTableItem } from "@/Documents/Models/Medical/MedicalDocumentTableItem";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";

import styles from "./BaseDocumentSearch.module.scss";

interface IBaseDocumentSearchProps {
    Model: IDocumentsTabModel;
    ColumnManagerModel: Immutable.List<ColumnConfigurationModel>;
    patient?: Patient;
}

class BaseDocumentSearchState {
    public ColumnOrder: string[];
    public approvingDocuments: string[];
    public showDocumentViewSettings: boolean;
    public selectedMedicalDocumentTableItem: MedicalDocumentTableItem;
    public selectedMedicalDocumentMouseEvent: React.MouseEvent;
}

abstract class BaseDocumentSearch extends BaseReactComponent<IBaseDocumentSearchProps, BaseDocumentSearchState> {
    protected helper = new MyleHelper();
    private updateClassesTimer: number;
    private resizeTimer: number;

    constructor(props: IBaseDocumentSearchProps) {
        super(props);

        this.state = {
            ColumnOrder: null,
            approvingDocuments: [],
            showDocumentViewSettings: false,
            selectedMedicalDocumentTableItem: null,
            selectedMedicalDocumentMouseEvent: null
        };
    }

    public componentDidMount(): void {
        this.updateClassesTimer = window.setTimeout(() => LocalEventDispatcher.dispatch(LocalOperations.Documents.SearchSettings_UpdateDocumentClassLocalizedNames), 200);
        this.resizeTimer = window.setTimeout(() => $(window).trigger("resize"), 200);
    }

    public componentWillUnmount(): void {
        clearInterval(this.updateClassesTimer);
        clearInterval(this.resizeTimer);
    }

    public getDocumentsTitle(model: IDocumentsTabModel): string {
        const view = model.get("SelectedView");

        switch (view) {
            case "allDocuments":
                const classModel = model.get("SelectedDocumentClass");
                const subclassModel = model.get("SelectedDocumentSubclass");

                if (subclassModel != null) {
                    const lookupClass = cacheStore.GetLookupById(classModel.LookupId);
                    const lookupSubclass = cacheStore.GetLookupById(subclassModel.LookupId);

                    const classModelText = DocumentClassModel.FromLookup(lookupClass);
                    const subClassModelText = DocumentClassModel.FromLookup(lookupSubclass);

                    return (classModelText.Name + " - " + subClassModelText.Name);
                }

                return cacheStore.GetString("AllDocumentsLabel");
            case "orphan":
                return cacheStore.GetString("OrphansLabel");
            case "deactivatedAccount":
                return cacheStore.GetString("DeactivatedAccountsLabel");
            default:
                break;
        }
    }

    protected onDocumentSelected(medicalDocumentTableItem: MedicalDocumentTableItem, mouseEvent: React.MouseEvent, showAlternativeDocumentView?: boolean): void {
        const isDocumentInAppViewEnabled = cacheStore.ClinicSettings.EnableDocumentInAppView;
        const userSettingsConfigView = cacheStore.UserSettings?.UserSettingsConfig?.ShowDocumentInAppView;
        const showDocumentInAppView =
            isDocumentInAppViewEnabled
            && (showAlternativeDocumentView ? !userSettingsConfigView : userSettingsConfigView);

        this.setState({
            selectedMedicalDocumentTableItem: medicalDocumentTableItem,
            selectedMedicalDocumentMouseEvent: mouseEvent
        });

        if (IsNullOrUndefined(showDocumentInAppView)) {
            this.setState({
                showDocumentViewSettings: true
            });
        }
        else if (showDocumentInAppView === false || !isDocumentInAppViewEnabled) {
            // open document in a new window
            sessionStore.dispatch(
                LocalOperations.Documents.Attachments_Load,
                {
                    target: MyleTableTarget.Myle_Documents_SearchResults_RowClick,
                    event: mouseEvent,
                    dataSourceItem: medicalDocumentTableItem,
                    rowData: medicalDocumentTableItem.Model
                }
            );
        }
        else {
            // display document in-app
            this.displayDocument(medicalDocumentTableItem, true);
        }
    }

    protected displayDocument(document: MedicalDocumentTableItem, annotationView: boolean = false): void {
        sessionStore.dispatch(LocalOperations.Documents.DisplayDocument, {
            docId: document.Model.Model.Id,
            annotationView: annotationView
        });
    }

    protected renderDocumentViewSettingPopup(): JSX.Element {
        return <DocumentViewSettingPopup onClose={() => this.closeDocumentViewSetting()} />;
    }

    protected onTaskTrailClick(e: React.MouseEvent, document: MedicalDocumentTableItem): void {
        e.stopPropagation();

        sessionStore.dispatch(
            LocalOperations.Documents.Search_TrailClick,
            {
                attachedCommunications: document.Model.Model.IndividualTaskLinkedItems,
                eventTarget: e.target,
                eventX: e.clientX,
                eventY: e.clientY
            }
        );
    }

    // Action
    protected buildActionColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        const padRight = { paddingRight: "3px" };
        const isDocumentViewSettingDefined = !IsNullOrUndefined(cacheStore.UserSettings.UserSettingsConfig?.ShowDocumentInAppView);

        return {
            renderCell: item => {
                const notesArray = item.Model.Model.NoteInfoCollection || [];

                const notesIndicatorText = ` ${notesArray.length || "+"} `;
                const notesIndicatorCss = `bold-text${(notesArray.length || item.Model.Model.IsUrgent ? " red" : "")}`;

                return (
                    <div className={`docs-actions ${styles.actionColumnLinks} ${item.Model.Model.IsUrgent ? "red-row" : ""}`}>{/* ? "flex-container docs-actions red-row" : "flex-container docs-actions "} style={{ alignItems: "center" }}> */}
                        <div className={styles.actionLinks}>
                            <a
                                aria-expanded="false"
                                onClick={e => this.OpenNotes(item, e)}
                                data-dropdown={this.props.Model.get("NotesDropdownControlId")}
                            >
                                [<span id={`dmt_${item.Model.Model.Id}`} className={notesIndicatorCss}>{notesIndicatorText}</span>]
                            </a>
                            {item.Model.Model.CanBeApprovedByCurrentUser && (
                                <>
                                    <span>{" / "}</span>
                                    <a onClick={e => this.ApproveNote(item, e)} style={padRight}>{cacheStore.GetString("ApproveLabel")}</a>
                                </>
                            )}
                            <span>{" / "}</span>
                            <span>
                                <a onClick={e => this.ShowTaskWindow(item, e)} style={padRight}>
                                    {cacheStore.GetString("TaskLabel")}
                                </a>
                                {item.Model.Model.IndividualTaskLinkedItems.any() && (
                                    <span
                                        style={{ marginLeft: "5px", cursor: "pointer" }}
                                        onClick={e => this.onTaskTrailClick(e, item)}
                                        className={item.Model.Model.AreAllTasksCompleted ? "circle-green" : "circle-orange"}
                                    >
                                        {item.Model.Model.IndividualTaskLinkedItems.length}
                                    </span>
                                )}
                            </span>
                            {cacheStore.ClinicSettings.EnableDocumentInAppView && isDocumentViewSettingDefined && (
                                <>
                                    <span>{" / "}</span>
                                    <a
                                        onClick={event => this.onDocumentSelected(item, event, true)}
                                        style={padRight}
                                    >
                                        {cacheStore.UserSettings.UserSettingsConfig.ShowDocumentInAppView ? cacheStore.GetString("DocumentSearch_PopOutLabel") : cacheStore.GetString("DocumentSearch_AnnotateLabel")}
                                    </a>
                                </>
                            )}
                        </div>
                        <InfoIconControl
                            key="view-doc"
                            containerClassName={styles.documentSearchInfoIcon}
                            onClick={() => this.displayDocument(item)}
                            imgProps={{
                                alt: "view document",
                                id: item.Model.Model.Id + "_handle_files",
                                "aria-expanded": "false",
                                "aria-controls": "AttachmentsDrop",
                                "data-dropdown": "AttachmentsDrop",
                                style: { height: "22px", margin: 0 }
                            }}
                        />
                    </div>
                );
            }
        };
    }

    private ApproveNote(item: MedicalDocumentTableItem, e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();

        const id = this.state.approvingDocuments.filter(c => c === item.Model.Model.Id).firstOrDefault();
        if (!id) {
            sessionStore.dispatch(LocalOperations.Documents.Approve, { DocumentIDs: [item.Model.Model.Id] });

            this.mutateState(s => {
                s.approvingDocuments = this.state.approvingDocuments;
                s.approvingDocuments.push(item.Model.Model.Id);
            });
        }
    }

    protected OpenNotes(item: MedicalDocumentTableItem, e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        sessionStore.dispatch(LocalOperations.Documents.Note_Load, {
            item: item,
            event: e
        });
    }

    private ShowTaskWindow(document: MedicalDocumentTableItem, e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        sessionStore.dispatch(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, {
            isTask: true,
            document: document
        });
    }

    private closeDocumentViewSetting(): void {
        this.setState({ showDocumentViewSettings: false });

        this.onDocumentSelected(this.state.selectedMedicalDocumentTableItem, this.state.selectedMedicalDocumentMouseEvent);
    }
}

export { BaseDocumentSearch, BaseDocumentSearchState, IBaseDocumentSearchProps };
